import Phaser from 'phaser';
import { GameSettings } from './GameSettings';

/**
 * A utility class for monitoring and displaying the actual FPS in-game
 */
export class FpsMonitor {
  private scene: Phaser.Scene;
  private fpsText: Phaser.GameObjects.Text;
  private frameCount: number = 0;
  private lastTime: number = 0;
  private fps: number = 0;
  private targetFps: number = 60;
  private settingsManager: GameSettings;
  private updateInterval: number = 500; // Update every 500ms for more stable readings
  private enabled: boolean = false;
  
  constructor(scene: Phaser.Scene) {
    this.scene = scene;
    this.settingsManager = GameSettings.getInstance();
    
    // First, check for and remove any existing FPS monitors in this scene
    this.removeExistingMonitors();
    
    // Create FPS text display in the top-right corner with higher depth to ensure visibility
    this.fpsText = this.scene.add.text(
      this.scene.cameras.main.width - 20, 
      20, 
      'FPS: 0', 
      { 
        font: '16px Arial',
        color: '#ffff00',
        backgroundColor: '#00000080', // Semi-transparent background for better visibility
        padding: { x: 5, y: 2 },      // Add padding
        stroke: '#000000',            // Text outline for better visibility
        strokeThickness: 2
      }
    ).setOrigin(1, 0)
     .setScrollFactor(0)    // Fixed position regardless of camera movement
     .setDepth(10000)       // Very high depth to ensure it's on top
     .setAlpha(1);          // Ensure full opacity
    
    console.log(`Created FPS monitor at position: ${this.fpsText.x}, ${this.fpsText.y} with depth ${this.fpsText.depth}`);
    
    // Set initial visibility based on settings
    this.enabled = this.settingsManager.getSettings().showFps;
    this.fpsText.setVisible(this.enabled);
    
    // Get target FPS
    this.targetFps = this.settingsManager.getSettings().fps;
    
    // Listen for display toggle
    this.scene.events.on('toggleFpsDisplay', this.toggleDisplay, this);
    
    // Listen for FPS rate changes
    this.scene.events.on('fpsRateChanged', this.onFpsRateChanged, this);
    
    // Add update callback
    this.scene.events.on('update', this.update, this);
    
    // Make sure text stays in the correct position when the game resizes
    this.scene.scale.on('resize', this.handleResize, this);
  }
  
  /**
   * Handle FPS rate changes
   */
  onFpsRateChanged(fps: number): void {
    console.log(`FpsMonitor received fpsRateChanged event: ${fps}`);
    this.targetFps = fps;
  }
  
  /**
   * Remove any existing FPS monitors in the scene
   */
  private removeExistingMonitors(): void {
    // Look for text objects that might be previous FPS monitors
    this.scene.children.list.forEach(child => {
      if (child instanceof Phaser.GameObjects.Text) {
        const text = child as Phaser.GameObjects.Text;
        if (text.text.includes('FPS:') && text !== this.fpsText) {
          console.log('Removing existing FPS monitor:', text.text);
          text.destroy();
        }
      }
    });
  }
  
  /**
   * Update the FPS counter
   */
  update(time: number): void {
    if (!this.enabled) return;
    
    this.frameCount++;
    
    // Only update the display periodically for stability
    if (time > this.lastTime + this.updateInterval) {
      // Calculate the actual FPS based on frame count and elapsed time
      this.fps = Math.round((this.frameCount / (time - this.lastTime)) * 1000);
      
      // Update the text with current, target, and game loop FPS
      const actualFps = this.scene.game.loop.actualFps.toFixed(1);
      const targetFps = this.scene.game.loop.targetFps;
      const deltaTime = this.scene.game.loop.delta;
      const frameDuration = 1000 / targetFps; // Calculate ideal frame duration
      
      let color = '#ffff00'; // Default yellow
      if (this.fps < targetFps * 0.9) {
        color = '#ff0000'; // Red when significantly below target
      } else if (this.fps > targetFps * 1.1) {
        color = '#00ff00'; // Green when significantly above target
      }
      
      // Format with more details
      // Format: FPS: [actual] (Target: [target], Phaser: [game.actualFps])
      const fpsText = `FPS: ${this.fps} (Target: ${targetFps}, Phaser: ${actualFps})`;
      
      // Add delta time - this can help diagnose if frames are taking too long
      const deltaText = `\nΔt: ${deltaTime.toFixed(1)}ms (Ideal: ${frameDuration.toFixed(1)}ms)`;
      
      this.fpsText.setText(fpsText + deltaText);
      this.fpsText.setColor(color);
      
      // Reset counters
      this.lastTime = time;
      this.frameCount = 0;
    }
  }
  
  /**
   * Toggle the display on/off
   */
  toggleDisplay(show?: boolean): void {
    if (show !== undefined) {
      this.enabled = show;
    } else {
      this.enabled = !this.enabled;
    }
    
    this.fpsText.setVisible(this.enabled);
    console.log(`FPS Display ${this.enabled ? 'enabled' : 'disabled'}`);
  }
  
  /**
   * Check if the FPS display is currently visible
   */
  isVisible(): boolean {
    return this.enabled;
  }
  
  /**
   * Update position when game resizes
   */
  handleResize(): void {
    if (!this.fpsText || !this.scene) {
      console.warn('FPS Monitor: Cannot reposition due to missing elements');
      return;
    }

    try {
      // Get the width from different sources in case one is undefined
      let width = 0;
      let height = 0;

      // First try to get size from the camera
      if (this.scene.cameras && this.scene.cameras.main) {
        width = this.scene.cameras.main.width;
        height = this.scene.cameras.main.height;
        console.log('FPS Monitor: Using camera dimensions for repositioning');
      } 
      // If camera is undefined, try the game scale 
      else if (this.scene.scale) {
        width = this.scene.scale.width;
        height = this.scene.scale.height;
        console.log('FPS Monitor: Using scene scale for repositioning');
      }
      // Use game dimension as fallback
      else if (this.scene.game && this.scene.game.scale) {
        width = this.scene.game.scale.width;
        height = this.scene.game.scale.height;
        console.log('FPS Monitor: Using game scale for repositioning');
      }
      // Last resort, use window dimensions
      else {
        width = window.innerWidth;
        height = window.innerHeight;
        console.log('FPS Monitor: Using window dimensions for repositioning');
      }

      // Check if we have valid dimensions
      if (width <= 0 || height <= 0) {
        console.warn('FPS Monitor: Invalid dimensions, using window size as fallback');
        width = window.innerWidth || 800;
        height = window.innerHeight || 600;
      }

      // Reposition the FPS counter to the top right of the screen
      this.fpsText.setPosition(width - 20, 20);
      console.log(`FPS monitor repositioned to: ${this.fpsText.x}, ${this.fpsText.y}`);
    } catch (error) {
      console.error('FPS Monitor: Error during resize handling:', error);
      // Emergency fallback - position at a safe location
      this.fpsText.setPosition(10, 10);
    }
  }
  
  /**
   * Clean up resources
   */
  destroy(): void {
    this.scene.events.off('update', this.update, this);
    this.scene.events.off('toggleFpsDisplay', this.toggleDisplay, this);
    this.scene.events.off('fpsRateChanged', this.onFpsRateChanged, this);
    this.scene.scale.off('resize', this.handleResize, this);
    
    if (this.fpsText) {
      this.fpsText.destroy();
    }
  }
} 